import { useQuery } from '@tanstack/react-query';
import { withAppHOC } from '../../hoc';
import { StyledContentContainer } from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import { MessageBox } from '../../components/FormElements';
import NoData from '../../components/NoData/NoData';
import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import styled from 'styled-components';
import { useEffect, useState, useMemo } from 'react';

/* ---------------- Layout ---------------- */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/* ---------------- Top Section ---------------- */

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-left: 15px;
  padding-top: 15px;
  align-items: center;
  margin-bottom: 10px;
`;

const MapCard = styled.div`
  height: 120px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e0f2fe, #f8fafc);
  border: 1px solid #e5e7eb;
  position: relative;
  overflow: hidden;
`;

const MapDot = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59,130,246,0.15);
`;

const LogoutAll = styled.button`
  background: #111827;
  color: #fff;
  border: none;
  padding: 10px 14px;
  border-radius: 10px;
  margin-right: 15px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

/* ---------------- Device UI ---------------- */

const DeviceIcon = styled.span`
  font-size: 16px;
`;

const DeviceGroup = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
`;

const DeviceHeader = styled.div`
  padding: 10px 12px;
  background: #f9fafb;
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  font-size: 13px;
`;

const SessionRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-top: 1px solid #f1f5f9;
  align-items: center;

  &:hover {
    background: #f9fafb;
  }
`;

/* ---------------- Status dot (NO ANIMATION) ---------------- */

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background: ${(p) => (p.current ? '#22c55e' : '#3b82f6')};
`;

/* ---------------- Pills ---------------- */

const Pill = styled.span`
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
`;

/* ---------------- Helpers ---------------- */

const getDeviceIcon = (device) => {
  if (!device) return '💻';
  const d = device.toLowerCase();
  if (d.includes('iphone')) return '📱';
  if (d.includes('mac')) return '🍎';
  if (d.includes('windows')) return '🪟';
  if (d.includes('android')) return '🤖';
  return '💻';
};

const groupByDevice = (sessions) => {
  return sessions.reduce((acc, s) => {
    const key = s.device || 'Unknown Device';
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});
};

/* ---------------- Page ---------------- */

const Sessions = () => {
  const fetchData = async () => {
    const res = await API.get('/authentication/sessions');
    return res.data;
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sessions'],
    queryFn: fetchData,
  });

  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (data?.data) setSessions(data.data);
  }, [data]);

  const logout = async (token) => {
    setSessions((prev) =>
      prev.filter((s) => s.sessionToken !== token)
    );

    try {
      await API.delete(`/authentication/logout?token=${token}`);
    } finally {
      refetch();
    }
  };

  const logoutAllOthers = async () => {
    const others = sessions.filter((s) => !s.isCurrent);

    setSessions((prev) => prev.filter((s) => s.isCurrent));

    await Promise.all(
      others.map((s) =>
        API.delete(`/authentication/logout?token=${s.sessionToken}`)
      )
    );

    refetch();
  };

  const grouped = useMemo(() => groupByDevice(sessions), [sessions]);

  const Header = () => <PageHeader title="Login & Security" />;

  if (isLoading) {
    return (
      <>
        <Header />
        <StyledContentContainer>Loading...</StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Header />
      <StyledContentContainer>

        {/* TOP BAR */}
        <TopBar>
          <div>
            <strong>Where you're logged in</strong>
          </div>
          <LogoutAll onClick={logoutAllOthers}>
            Logout All
          </LogoutAll>
        </TopBar>

        {sessions.length === 0 ? (
          <NoData />
        ) : (
          <Wrapper>
            {Object.entries(grouped).map(([device, list]) => (
              <DeviceGroup key={device}>
                <DeviceHeader>
                  <span>
                    <DeviceIcon>
                      {getDeviceIcon(device)}
                    </DeviceIcon>{' '}
                    {device}
                  </span>
                  <span>{list.length} session(s)</span>
                </DeviceHeader>

                {list.map((s) => (
                  <SessionRow key={s.sessionToken}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Dot current={s.isCurrent} />

                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {s.location || 'Unknown location'}
                        </div>

                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          IP: {s.ipAddress || 'N/A'} • {s.lastActive || 'Active now'}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {s.isCurrent && <Pill>You</Pill>}

                      {!s.isCurrent && (
                        <button
                          onClick={() => logout(s.sessionToken)}
                          style={{
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            padding: '6px 10px',
                            borderRadius: 8,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                        >
                          Logout
                        </button>
                      )}
                    </div>
                  </SessionRow>
                ))}
              </DeviceGroup>
            ))}
          </Wrapper>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(Sessions);